import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { InvitePayScheme } from '@vyce/core/src/api/types';
import { PaySchedule, Team, TimeAndAttendanceLocation } from '@vyce/core/src/types';
import { toggleId } from '@vyce/core/src/utils/staffPermissions';

import { InviteModuleKey, InvitePreselectedTarget, TargetsState } from '../types';

interface Options {
  teams?: Team[];
  locations?: TimeAndAttendanceLocation[];
  paySchedules?: PaySchedule[];
  /** The inventories above have finished loading — empty lists before that are not a verdict */
  resourcesReady: boolean;
  /** Dialog visibility — a preselection is applied once per opening */
  open: boolean;
  /** The resource the dialog was opened from, ticked as soon as the options are in */
  preselect?: InvitePreselectedTarget;
  /** Targets prepared elsewhere (a Laila card), applied once the options are in */
  prefill?: TargetsState | null;
}

const EMPTY_TARGETS: TargetsState = {
  teamIds: [],
  locationIds: [],
  shiftIdsByLocation: {},
  payScheduleIds: [],
  payScheme: null,
};

// Deleted shifts are hidden in the list, so they must not be selectable through "All shifts" either
const shiftIdsOf = (location?: TimeAndAttendanceLocation): string[] =>
  (location?.shifts ?? [])
    .filter(shift => !shift.deleted_at)
    .map(shift => shift.uuid)
    .filter((id): id is string => !!id);

// Only resources with an id can be targets
const withId = <T extends { uuid?: string }>(items?: T[]): T[] => (items ?? []).filter(item => !!item.uuid);

/**
 * Where the selected people go. Every resource of the company is on offer — the invite routes
 * do not narrow by the caller's rights, so anyone can be invited anywhere.
 */
export const useInviteTargets = ({
  teams,
  locations,
  paySchedules,
  resourcesReady,
  open,
  preselect,
  prefill,
}: Options) => {
  const [state, setState] = useState<TargetsState>(EMPTY_TARGETS);
  const [preselectedModule, setPreselectedModule] = useState<InviteModuleKey | null>(null);
  // The inventories must be in before any section is shown or judged missing
  const ready = resourcesReady;

  const teamOptions = useMemo(() => withId(teams), [teams]);
  const locationOptions = useMemo(() => withId(locations), [locations]);
  const payScheduleOptions = useMemo(() => withId(paySchedules), [paySchedules]);

  const visibleModules = useMemo<InviteModuleKey[]>(() => {
    const modules: InviteModuleKey[] = [];
    if (teamOptions.length) modules.push('teams');
    if (locationOptions.length) modules.push('time');
    if (payScheduleOptions.length) modules.push('payroll');
    return modules;
  }, [teamOptions, locationOptions, payScheduleOptions]);

  const noModulesAvailable = ready && visibleModules.length === 0;

  const preselectKey = preselect ? `${preselect.module}:${preselect.resourceId}` : null;
  const appliedPreselectRef = useRef<string | null>(null);

  // The page the dialog was opened from is the obvious target: tick it as soon as the options
  // are in. A resource missing from the options (e.g. deleted meanwhile) is not preselected —
  // the "Where" step then behaves as if the dialog was opened from anywhere else.
  useEffect(() => {
    if (!open) {
      appliedPreselectRef.current = null;
      return;
    }
    if (!ready || !preselectKey || appliedPreselectRef.current === preselectKey) {
      return;
    }
    const { module, resourceId } = preselect as InvitePreselectedTarget;

    if (module === 'teams') {
      if (!teamOptions.some(team => team.uuid === resourceId)) {
        return;
      }
      setState(value => ({ ...value, teamIds: [resourceId] }));
    }

    if (module === 'time') {
      const location = locationOptions.find(item => item.uuid === resourceId);
      const shiftIds = shiftIdsOf(location);
      // A worker is always added to a shift — a location without one cannot be a target
      if (!location || !shiftIds.length) {
        return;
      }
      setState(value => ({
        ...value,
        locationIds: [resourceId],
        // One shift leaves no choice to make; with several the user picks, guided by the error
        shiftIdsByLocation: shiftIds.length === 1 ? { [resourceId]: shiftIds } : {},
      }));
    }

    if (module === 'payroll') {
      if (!payScheduleOptions.some(schedule => schedule.uuid === resourceId)) {
        return;
      }
      setState(value => ({ ...value, payScheduleIds: [resourceId] }));
    }

    appliedPreselectRef.current = preselectKey;
    setPreselectedModule(module);
  }, [open, ready, preselectKey, preselect, teamOptions, locationOptions, payScheduleOptions]);

  // Applied once per opening, like the preselection
  const appliedPrefillRef = useRef(false);

  // A prepared invite: keep what still exists, so a team deleted meanwhile is
  // simply not ticked, and open the first module it fills.
  useEffect(() => {
    if (!open) {
      appliedPrefillRef.current = false;
      return;
    }
    if (!ready || !prefill || appliedPrefillRef.current) {
      return;
    }
    const teamIds = prefill.teamIds.filter(id => teamOptions.some(team => team.uuid === id));
    const shiftIdsByLocation: Record<string, string[]> = {};
    prefill.locationIds.forEach(id => {
      const shiftIds = shiftIdsOf(locationOptions.find(item => item.uuid === id));
      const picked = (prefill.shiftIdsByLocation[id] ?? []).filter(shiftId => shiftIds.includes(shiftId));
      if (shiftIds.length) {
        shiftIdsByLocation[id] = picked;
      }
    });
    const locationIds = Object.keys(shiftIdsByLocation);
    const payScheduleIds = prefill.payScheduleIds.filter(id =>
      payScheduleOptions.some(schedule => schedule.uuid === id)
    );
    setState({ teamIds, locationIds, shiftIdsByLocation, payScheduleIds, payScheme: prefill.payScheme });
    setPreselectedModule(
      teamIds.length ? 'teams' : locationIds.length ? 'time' : payScheduleIds.length ? 'payroll' : null
    );
    appliedPrefillRef.current = true;
  }, [open, ready, prefill, teamOptions, locationOptions, payScheduleOptions]);

  const toggleTeam = useCallback(
    (id: string, checked: boolean) =>
      setState(value => ({ ...value, teamIds: toggleId(value.teamIds, id, checked) })),
    []
  );

  const toggleAllTeams = useCallback(
    (checked: boolean) =>
      setState(value => ({ ...value, teamIds: checked ? teamOptions.map(team => team.uuid) : [] })),
    [teamOptions]
  );

  const toggleLocation = useCallback(
    (id: string, checked: boolean) =>
      setState(value => {
        const shiftIdsByLocation = { ...value.shiftIdsByLocation };
        if (!checked) {
          delete shiftIdsByLocation[id];
        }
        return { ...value, locationIds: toggleId(value.locationIds, id, checked), shiftIdsByLocation };
      }),
    []
  );

  // Only locations that have shifts can be targets — a worker is always added to a shift
  const toggleAllLocations = useCallback(
    (checked: boolean) =>
      setState(value => ({
        ...value,
        locationIds: checked
          ? locationOptions
              .filter(location => shiftIdsOf(location).length)
              .map(location => location.uuid as string)
          : [],
        shiftIdsByLocation: checked ? value.shiftIdsByLocation : {},
      })),
    [locationOptions]
  );

  const toggleShift = useCallback(
    (locationId: string, shiftId: string, checked: boolean) =>
      setState(value => ({
        ...value,
        shiftIdsByLocation: {
          ...value.shiftIdsByLocation,
          [locationId]: toggleId(value.shiftIdsByLocation[locationId] ?? [], shiftId, checked),
        },
      })),
    []
  );

  const toggleAllShifts = useCallback(
    (checked: boolean) =>
      setState(value => {
        const shiftIdsByLocation: Record<string, string[]> = {};
        value.locationIds.forEach(locationId => {
          const location = locationOptions.find(item => item.uuid === locationId);
          shiftIdsByLocation[locationId] = checked ? shiftIdsOf(location) : [];
        });
        return { ...value, shiftIdsByLocation };
      }),
    [locationOptions]
  );

  const togglePaySchedule = useCallback(
    (id: string, checked: boolean) =>
      setState(value => ({ ...value, payScheduleIds: toggleId(value.payScheduleIds, id, checked) })),
    []
  );

  const toggleAllPaySchedules = useCallback(
    (checked: boolean) =>
      setState(value => ({
        ...value,
        payScheduleIds: checked ? payScheduleOptions.map(schedule => schedule.uuid as string) : [],
      })),
    [payScheduleOptions]
  );

  const setPayScheme = useCallback(
    (payScheme: InvitePayScheme | null) => setState(value => ({ ...value, payScheme })),
    []
  );

  const reset = useCallback(() => {
    setState(EMPTY_TARGETS);
    setPreselectedModule(null);
  }, []);

  const locationsMissingShift = useMemo(
    () => state.locationIds.filter(id => !state.shiftIdsByLocation[id]?.length),
    [state.locationIds, state.shiftIdsByLocation]
  );
  const payrollMissingScheme = state.payScheduleIds.length > 0 && !state.payScheme;
  const targetsCount = state.teamIds.length + state.locationIds.length + state.payScheduleIds.length;
  const isValid = ready && targetsCount > 0 && !locationsMissingShift.length && !payrollMissingScheme;

  return {
    state,
    ready,
    teamOptions,
    locationOptions,
    payScheduleOptions,
    visibleModules,
    /** Module of the applied preselection — the "Where" step opens it instead of the first one */
    preselectedModule,
    noModulesAvailable,
    toggleTeam,
    toggleAllTeams,
    toggleLocation,
    toggleAllLocations,
    toggleShift,
    toggleAllShifts,
    togglePaySchedule,
    toggleAllPaySchedules,
    setPayScheme,
    reset,
    locationsMissingShift,
    payrollMissingScheme,
    targetsCount,
    isValid,
  };
};

export type InviteTargetsApi = ReturnType<typeof useInviteTargets>;
